import { RoleType } from '@feature/security';

import { TravelOverlapRequest } from '../model/TravelRequestTypes';

export type TravelOverlapSource = Pick<TravelOverlapRequest, 'paperTitle'>;

// The overlaps endpoint answers 403 to every other role.
export const travelOverlapRoles = [
  RoleType.RESEARCH_ADMINISTRATIVE_OFFICE,
  RoleType.INTERNATIONAL_ADMINISTRATIVE_OFFICE,
  RoleType.RESEARCH_VICE_RECTOR,
  RoleType.INTERNATIONAL_VICE_RECTOR,
  RoleType.ADMIN,
  RoleType.UNIVERSITY_ADMIN,
] as const satisfies readonly RoleType[];

export function isTravelOverlapRole(role: RoleType) {
  return (travelOverlapRoles as readonly RoleType[]).includes(role);
}

export interface TravelOverlapWord {
  lead: string;
  core: string;
  trail: string;
  start: number;
  marked: boolean;
}

function normalizeWord(word: string) {
  return word
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');
}

function words(text: string) {
  return [...text.matchAll(/\S+/g)].map(match => ({ text: match[0], start: match.index }));
}

function normalizedWords(text: string) {
  return words(text)
    .map(word => normalizeWord(word.text))
    .filter(word => word.length > 0);
}

// Edge punctuation stays outside the highlight, so only the differing word itself is marked.
function splitEdges(text: string) {
  const [, lead, core, trail] = /^([^\p{L}\p{M}\p{N}]*)(.*?)([^\p{L}\p{M}\p{N}]*)$/su.exec(text) ?? ['', '', text, ''];
  return { lead, core, trail };
}

function markAbsent(text: string, other: Set<string>): TravelOverlapWord[] {
  return words(text).map(word => {
    const normalized = normalizeWord(word.text);
    return { ...splitEdges(word.text), start: word.start, marked: normalized.length > 0 && !other.has(normalized) };
  });
}

export function diffWords(left: string, right: string): [TravelOverlapWord[], TravelOverlapWord[]] {
  const leftSet = new Set(normalizedWords(left));
  const rightSet = new Set(normalizedWords(right));
  return [markAbsent(left, rightSet), markAbsent(right, leftSet)];
}

// Order and repetition count here, unlike the highlight, so reordered titles still show both sides.
export function sameWords(left: string, right: string) {
  const leftWords = normalizedWords(left);
  const rightWords = normalizedWords(right);
  return leftWords.length === rightWords.length && leftWords.every((word, index) => word === rightWords[index]);
}
